/**
 * `/gallery/player-season` exists so
 * `tests/e2e/src/app/playerSeasonGeometry.e2e.ts` can measure the player
 * page's Stats grid and Game log in a real browser (ISSUE-1188). It is signed
 * in and linked from no navigation, following the `/gallery/box-score`
 * precedent. The file is named `gallery_.player-season` so this route stays
 * out of `/gallery`'s tree, which renders no Outlet.
 */
import { createFileRoute } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { requireSignedIn } from "../auth/requireSignedIn";
import { PlayerGameLogTable } from "../features/playerProfile/PlayerGameLogTable";
import { PlayerStatsPanel } from "../features/playerProfile/PlayerStatsPanel";
import type {
  typePlayerGameLogGame,
  typePlayerGameLogTable,
  typePlayerStatGroup,
} from "../features/playerProfile/playerSeasonView";

const fact = (
  statKey: string,
  label: string,
  displayValue: string,
  rankLabel: string | null = null,
) => ({ statKey, label, displayValue, rankLabel });

const GROUPS: typePlayerStatGroup[] = [
  {
    groupKey: "passing",
    label: "Passing",
    stats: [
      fact("gamesPlayed", "GP", "17"),
      fact("completions", "CMP", "401"),
      fact("attempts", "ATT", "584"),
      fact("yards", "YDS", "4,183", "3rd"),
      fact("touchdowns", "TD", "31", "T2nd"),
      fact("interceptions", "INT", "11"),
      fact("yardsPerGame", "YPG", "246.1"),
      fact("rating", "RTG", "101.4"),
      fact("longest", "LNG", "68"),
    ],
  },
  {
    groupKey: "rushing",
    label: "Rushing",
    stats: [
      fact("gamesPlayed", "GP", "17"),
      fact("attempts", "CAR", "61"),
      fact("yards", "YDS", "389"),
      fact("touchdowns", "TD", "4"),
    ],
  },
];

const GAMES: typePlayerGameLogGame[] = [
  {
    eventId: "evt-1",
    dateLabel: "Oct 19",
    opponentLabel: "vs AUS",
    resultCode: "W",
    scoreLabel: "112-104",
  },
  {
    eventId: "evt-2",
    dateLabel: "Oct 17",
    opponentLabel: "at POR",
    resultCode: "L",
    scoreLabel: "99-101",
  },
  {
    eventId: "evt-3",
    dateLabel: "Oct 15",
    opponentLabel: "vs POR",
    resultCode: "W",
    scoreLabel: "120-98",
  },
];

const COLUMNS = [
  { key: "minutes", label: "MIN" },
  { key: "points", label: "PTS" },
  { key: "fieldGoals", label: "FG" },
  { key: "threePointers", label: "3PT" },
  { key: "freeThrows", label: "FT" },
  { key: "rebounds", label: "REB" },
  { key: "assists", label: "AST" },
  { key: "steals", label: "STL" },
  { key: "blocks", label: "BLK" },
  { key: "turnovers", label: "TO" },
];

const row = (eventId: string, values: string[]) => ({
  eventId,
  cells: COLUMNS.map((column, index) => ({
    key: column.key,
    display: values[index] ?? "-",
  })),
});

const TABLE: typePlayerGameLogTable = {
  key: "overall",
  label: "Players",
  columns: COLUMNS,
  rows: [
    row("evt-1", ["34", "31", "11/20", "3/7", "6/6", "9", "10", "2", "1", "3"]),
    row("evt-2", ["36", "24", "9/21", "1/6", "5/6", "12", "7", "1", "0", "4"]),
    row("evt-3", ["30", "28", "10/16", "2/4", "6/7", "8", "11", "3", "2", "2"]),
  ],
};

const PlayerSeasonGalleryScreen = (): ReactNode => (
  <div
    data-testid="player-season-gallery"
    className="flex flex-col gap-6 px-page-gutter py-4"
  >
    <h1 className="text-h1 text-ink">Player season gallery</h1>
    <p className="text-body text-ink-secondary">
      The player page's Stats cards and Game log at phone and desktop width.
      This page exists so a browser can measure them. It is not linked from any
      navigation.
    </p>
    <div data-testid="player-season-gallery-stats">
      <PlayerStatsPanel groups={GROUPS} />
    </div>
    <div data-testid="player-season-gallery-game-log">
      <PlayerGameLogTable table={TABLE} games={GAMES} />
    </div>
  </div>
);

export const Route = createFileRoute("/gallery_/player-season")({
  beforeLoad: requireSignedIn,
  component: PlayerSeasonGalleryScreen,
});
